<script lang="ts">import { t, Verbiage } from "@infinitro/content";
import { loggedIn, ModalDialog, pushModal, statePlayerId } from "@infinitro/global";
import { Text } from "@infinitro/text";
import { createEventDispatcher, onMount } from "svelte";
import { createOrUpdateReview, getGameReviews } from "../../api";
export let game = undefined;
const dispatch = createEventDispatcher();
let selectedRating = 0;
let hoveredRating = 0;
let userComment = "";
let isSubmitting = false;
let successMessage = "";
let errorMessage = "";
let isLoadingExistingReview = false;
let currentGameSlug = undefined;
let hasLoadedForCurrentGame = false;
// Function to load user's existing review
async function loadExistingReview() {
	if (!game || !game.game_slug || !game.provider_slug) {
		return;
	}
	if (!$loggedIn || !$statePlayerId) {
		// Don't reset if user not logged in - just skip loading
		return;
	}
	try {
		isLoadingExistingReview = true;
		const reviews = await getGameReviews(game.game_slug, game.provider_slug);
		// Find the current user's review
		const userReview = reviews.find((review) => review.playerId === $statePlayerId);
		if (userReview) {
			// Convert rating from 0-10 scale back to 1-5 stars
			// Rating 0-10 maps to 0-5 stars (divide by 2)
			const stars = userReview.rating / 2;
			selectedRating = Math.ceil(stars);
			userComment = userReview.reviewText || "";
		} else {
			// Reset if no existing review
			selectedRating = 0;
			userComment = "";
		}
		// Mark that we've loaded for this game
		hasLoadedForCurrentGame = true;
	} catch (error) {
		console.warn("Error loading existing review:", error);
	} finally {
		isLoadingExistingReview = false;
	}
}
// Load on mount
onMount(() => {
	currentGameSlug = game?.game_slug;
	// Try to load immediately on mount
	if (game?.game_slug && $loggedIn && $statePlayerId) {
		loadExistingReview();
	} else {}
});
// Watch for game changes or login state changes
// This catches cases where playerId loads after mount
$: if (game?.game_slug && $loggedIn && $statePlayerId) {
	if (game.game_slug !== currentGameSlug) {
		currentGameSlug = game.game_slug;
		hasLoadedForCurrentGame = false;
		loadExistingReview();
	} else if (!isLoadingExistingReview && !hasLoadedForCurrentGame) {
		// If we haven't loaded yet for this game, try to load
		loadExistingReview();
	}
}
async function handleStarClick(rating) {
	if (!$loggedIn) {
		pushModal(ModalDialog.Auth, { tab: "login" });
		return;
	}
	if (!game) {
		errorMessage = $t(Verbiage.game_info_not_available);
		return;
	}
	isSubmitting = true;
	successMessage = "";
	errorMessage = "";
	// Store the rating we're submitting
	const submittedRating = rating;
	try {
		// Convert rating from 1-5 to 0-10 scale
		const scaledRating = submittedRating * 2;
		const result = await createOrUpdateReview({
			gameSlug: game.game_slug || "",
			providerSlug: game.provider_slug || "",
			rating: scaledRating,
			reviewText: userComment.trim() || undefined
		});
		if (result.success) {
			// Update UI immediately with the submitted value
			selectedRating = submittedRating;
			successMessage = result.message;
			// Notify parent component that review was updated (triggers average rating refresh)
			dispatch("reviewUpdated", { rating: scaledRating });
			// Clear success message after 3 seconds
			setTimeout(() => {
				successMessage = "";
			}, 3e3);
		} else {
			errorMessage = result.message;
			// Reload on error to ensure we show the correct current state
			await loadExistingReview();
		}
	} catch (error) {
		console.warn("Error submitting review:", error);
		errorMessage = $t(Verbiage.failed_submit_review);
		// Reload on error to ensure we show the correct current state
		await loadExistingReview();
	} finally {
		isSubmitting = false;
	}
}
function handleStarHover(rating) {
	hoveredRating = rating;
}
function handleStarLeave() {
	hoveredRating = 0;
}
function getRatingText(rating, $t) {
	if (rating === 0) return "";
	const ratings = [
		"",
		$t(Verbiage.reviews_rating_poor),
		$t(Verbiage.reviews_rating_fair),
		$t(Verbiage.reviews_rating_good),
		$t(Verbiage.reviews_rating_very_good),
		$t(Verbiage.reviews_rating_amazing)
	];
	return ratings[rating];
}
</script>

<div class="rate-game-container">
	{#if isLoadingExistingReview}
		<div class="loading-state">
			<Text type="body" bodySize="small" customStyle="color: var(--color-neutral-500);"
				>Loading your review...</Text
			>
		</div>
	{:else}
		<Text type="body" bodySize="medium" bodyWeight="bold"
			>{$t(Verbiage.reviews_rate_this_game)}</Text
		>

		<div class="rating-row">
			<div class="star-rating">
				{#each [1, 2, 3, 4, 5] as star}
					<button
						class="star"
						disabled={isSubmitting}
						on:click={() => handleStarClick(star)}
						on:mouseenter={() => handleStarHover(star)}
						on:mouseleave={handleStarLeave}
					>
						<svg
							class="rate-star"
							class:rate-star--filled={star <= (hoveredRating || selectedRating)}
							width="24"
							height="24"
							viewBox="0 0 24 24"
							stroke-width="2"
						>
							<path
								d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
							/>
						</svg>
					</button>
				{/each}
			</div>

			{#if selectedRating > 0}
				<div class="rating-text">
					<Text
						type="body"
						bodySize="small"
						customStyle="color: var(--color-primary-500); text-shadow: 0 0 10px color-mix(in srgb, var(--color-primary-500) 60%, transparent);"
						>{getRatingText(selectedRating, $t)}</Text
					>
				</div>
			{/if}
		</div>

		{#if successMessage}
			<div class="message success-message">
				<Text type="body" bodySize="small" customStyle="color: var(--color-primary-500);"
					>{successMessage}</Text
				>
			</div>
		{/if}

		{#if errorMessage}
			<div class="message error-message">
				<Text type="body" bodySize="small" customStyle="color: #FF9E14;">{errorMessage}</Text>
			</div>
		{/if}
	{/if}
</div>

<style>
	.rate-game-container {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		border-radius: 0.5rem;
		padding: 1rem;
		background: var(--color-neutral-900);
}

	.rating-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.75rem;
}

	.star-rating {
		display: flex;
		gap: 0.25rem;
}

	.rating-text {
		display: flex;
		align-items: center;
		justify-content: center;
}

	.star {
		cursor: pointer;
		border-style: none;
		background-color: transparent;
		padding: 0px;
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
}

	.star:hover:not(:disabled) {
		--tw-scale-x: 1.1;
		--tw-scale-y: 1.1;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.star:disabled {
		cursor: not-allowed;
		opacity: 0.5;
}

	.rate-star {
		fill: var(--color-neutral-100);
		stroke: var(--color-neutral-100);
	}

	.rate-star--filled {
		fill: var(--color-primary-500);
		stroke: var(--color-primary-500);
		filter: drop-shadow(0 0 4px color-mix(in srgb, var(--color-primary-500) 60%, transparent));
	}

	.message {
		border-radius: 0.5rem;
		padding: 0.5rem;
		text-align: center;
}

	.success-message {
		background: color-mix(in srgb, var(--color-primary-500) 10%, transparent);
	}

	.error-message {
		background: rgba(255, 158, 20, 0.1);
	}

	.loading-state {
		display: flex;
		align-items: center;
		justify-content: center;
		padding-top: 1rem;
		padding-bottom: 1rem;
}
</style>
